import React, { useState, useCallback, useMemo, useEffect, useLayoutEffect } from 'react';
import { useAppMenuSearch } from '../../context/AppMenuSearchContext';
import { tabMatchesMenuSearch } from '../../utils/tabSearchUtils';

/**
 * Reusable Tabs component – uncontrolled or controlled.
 *
 * @param {Array<{ id: string, label: React.ReactNode, disabled?: boolean, icon?: React.ReactNode, badge?: number, children: React.ReactNode, description?: string, tooltip?: string, ariaLabel?: string, searchKeywords?: string[] }>} tabs - Tab configs; with `iconOnly`, `description` or `tooltip` is shown as the native hover title.
 * @param {string} [defaultTabId] - Initial active tab (uncontrolled). If omitted, first tab is used.
 * @param {string} [activeTabId] - Controlled active tab. When set, use with onTabChange.
 * @param {function(string): void} [onTabChange] - Called when tab changes (id). Use for controlled mode.
 * @param {string} [className] - Extra class for the outer wrapper.
 * @param {string} [tabListClassName] - Extra class for the tab list (nav) container.
 * @param {string} [panelClassName] - Extra class for the active panel container.
 * @param {boolean} [iconOnly=false] - When true, show only icons; label appears on hover via tooltip.
 * @param {boolean} [respectAppMenuSearch=true] - When inside AppLayout, filter tabs by the sidebar menu search query.
 */
const Tabs = ({
  tabs = [],
  defaultTabId,
  activeTabId: controlledActiveTabId,
  onTabChange,
  className = '',
  tabListClassName = '',
  tabListRight = null,
  panelClassName = '',
  iconOnly = false,
  respectAppMenuSearch = true,
}) => {
  const { query: menuSearchQuery, preferredTabId, setPreferredTabId } = useAppMenuSearch();
  const queryLower = useMemo(() => {
    if (!respectAppMenuSearch) return '';
    return String(menuSearchQuery ?? '').trim().toLowerCase();
  }, [respectAppMenuSearch, menuSearchQuery]);

  const visibleTabs = useMemo(() => {
    if (!queryLower) return tabs;
    return tabs.filter((t) => tabMatchesMenuSearch(t, queryLower));
  }, [tabs, queryLower]);

  const firstId = visibleTabs.length > 0 ? visibleTabs[0].id : null;
  const [internalActiveId, setInternalActiveId] = useState(defaultTabId ?? firstId);

  const isControlled = controlledActiveTabId !== undefined && controlledActiveTabId !== null;
  const activeId = isControlled ? controlledActiveTabId : internalActiveId;
  const effectiveActiveId = activeId ?? firstId;

  /** Apply tab chosen by AppLayout auto-navigation (sidebar search). Runs before paint. */
  useLayoutEffect(() => {
    if (!respectAppMenuSearch) return;
    if (!preferredTabId || !queryLower) return;
    const match = visibleTabs.find((t) => String(t.id) === String(preferredTabId));
    if (!match) {
      setPreferredTabId(null);
      return;
    }
    if (String(effectiveActiveId) === String(preferredTabId)) {
      setPreferredTabId(null);
      return;
    }
    if (isControlled) {
      onTabChange?.(preferredTabId);
    } else {
      setInternalActiveId(preferredTabId);
      onTabChange?.(preferredTabId);
    }
    setPreferredTabId(null);
  }, [
    preferredTabId,
    visibleTabs,
    queryLower,
    effectiveActiveId,
    respectAppMenuSearch,
    isControlled,
    onTabChange,
    setPreferredTabId,
  ]);

  /** If the active tab is hidden by the menu search, switch to the first visible tab. */
  useEffect(() => {
    if (!queryLower || visibleTabs.length === 0) return;
    const visibleIds = new Set(visibleTabs.map((t) => t.id));
    if (visibleIds.has(effectiveActiveId)) return;
    const next = visibleTabs[0].id;
    if (isControlled) {
      onTabChange?.(next);
    } else {
      setInternalActiveId(next);
      onTabChange?.(next);
    }
  }, [queryLower, visibleTabs, effectiveActiveId, isControlled, onTabChange]);

  const handleSelect = useCallback(
    (id) => {
      if (isControlled) {
        onTabChange?.(id);
      } else {
        setInternalActiveId(id);
        onTabChange?.(id);
      }
    },
    [isControlled, onTabChange]
  );

  if (tabs.length === 0) return null;

  if (visibleTabs.length === 0) {
    return (
      <div className={`flex flex-col ${className}`.trim()}>
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          No section titles match this menu search. Clear or change the search in the sidebar to see all tabs.
        </p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`.trim()}>
      <div className={`flex items-end justify-between border-b border-slate-200/80 ${tabListClassName}`.trim()}>
        <div role="tablist" aria-label="Tabs" className="flex flex-wrap gap-0">
          {visibleTabs.map((tab) => {
            const isActive = tab.id === effectiveActiveId;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`tab-${tab.id}`}
                aria-selected={isActive}
                aria-controls={`panel-${tab.id}`}
                aria-label={
                  iconOnly
                    ? String(tab.ariaLabel ?? tab.label ?? tab.id ?? '')
                    : undefined
                }
                title={
                  iconOnly
                    ? String(tab.description ?? tab.tooltip ?? tab.label ?? '')
                    : undefined
                }
                tabIndex={isActive ? 0 : -1}
                disabled={tab.disabled}
                onClick={() => !tab.disabled && handleSelect(tab.id)}
                className={`
                  relative ${iconOnly ? 'px-3 py-2.5' : 'px-4 py-2.5'} text-sm font-medium transition-colors duration-150
                  focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-primary-dark/30 focus-visible:ring-offset-2
                  disabled:cursor-not-allowed disabled:opacity-40
                  ${isActive ? 'text-primary -mb-px bg-surface-muted' : 'text-slate-500 hover:text-slate-700'}
                `}
              >
                <span className={`inline-flex items-center ${iconOnly ? 'justify-center' : 'gap-2'}`}>
                  {tab.icon != null && (
                    <span className={`shrink-0 [&>svg]:size-4 ${iconOnly ? '[&>svg]:size-5' : ''}`} aria-hidden>
                      {tab.icon}
                    </span>
                  )}
                  {!iconOnly && tab.label}
                  {tab.badge != null && (
                    <span
                      className={`min-w-[1.25rem] px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                        isActive ? 'bg-primary/20 text-primary' : 'bg-slate-200 text-slate-700'
                      }`}
                      aria-label={`${tab.badge} items`}
                    >
                      {tab.badge}
                    </span>
                  )}
                </span>
                {isActive && <span className="absolute inset-x-0 bottom-0 h-[2px] bg-primary rounded-full" aria-hidden />}
              </button>
            );
          })}
        </div>
        {tabListRight ? <div className="pb-2.5 pl-3">{tabListRight}</div> : null}
      </div>
      <div
        role="tabpanel"
        id={visibleTabs.find((t) => t.id === effectiveActiveId) ? `panel-${effectiveActiveId}` : undefined}
        aria-labelledby={
          visibleTabs.find((t) => t.id === effectiveActiveId) ? `tab-${effectiveActiveId}` : undefined
        }
        className={`flex min-h-0 flex-1 flex-col overflow-y-auto pt-4 ${panelClassName}`.trim()}
      >
        {visibleTabs.map((tab) =>
          tab.id === effectiveActiveId ? (
            <div key={tab.id} className="flex min-h-0 min-w-0 flex-1 flex-col">
              {tab.children}
            </div>
          ) : null
        )}
      </div>
    </div>
  );
};

export default Tabs;
